/**
 * ColumnTemplatesEditor — editor de las vistas fijas por pantalla (PT-#67).
 *
 * Dos templates hoy: PUBLICACIONES (impacta listado + export de Publicaciones) y
 * DATA EXPORT (base del módulo Exportar). Cada uno es una lista de columnas con
 * on/off + orden (drag&drop, se ve el #ordinal). El Registro (core/native/tags)
 * determina QUÉ columnas están disponibles; acá se elige cuáles y en qué orden.
 *
 * El template se embebe en columnConfig.templates[screenId] (ver columnTemplates.ts).
 * Preview: renderiza DataTable en previewMode aplicando el orden/visibilidad del
 * template (estilo panel PUBS).
 */
import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { GripVertical, Eye, EyeOff, Loader2, Layers } from 'lucide-react';
import {
  DndContext,
  DragEndEvent,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ColumnConfigDoc } from '../../services/columnConfig';
import {
  ColumnTemplate,
  ColumnTemplateScreenId,
  ColumnTemplatesMap,
  TEMPLATE_SCREENS,
  reconcileTemplateItems,
  resolveTemplateOrder,
} from '../../services/columnTemplates';
import { DATA_EXPORT_BASE_ORDER, DEFAULT_EXPORT_ATTRS } from '../../services/columnExportAttrs';
import DataTable from '../DataTable';
import previewSample from '../../data/columnConfigPreviewSample.json';
import api from '../../utils/api';

interface Props {
  config: ColumnConfigDoc;
  clientId: string;
  lastTomaScanned: string | null;
  onChange: (templates: ColumnTemplatesMap) => void;
}

interface AvailableColumn {
  id: string;
  label: string;
  group: 'core' | 'native' | 'tags' | 'calc';
}

const GROUP_LABEL: Record<AvailableColumn['group'], string> = {
  core: 'Core',
  native: 'Native',
  tags: 'Tag',
  calc: 'Calculada',
};

const GROUP_BADGE: Record<AvailableColumn['group'], string> = {
  core: 'bg-blue-50 text-blue-700 border-blue-200',
  native: 'bg-purple-50 text-purple-700 border-purple-200',
  tags: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  calc: 'bg-amber-50 text-amber-700 border-amber-200',
};

/**
 * Deriva las columnas DISPONIBLES para un template desde el Registro (columnConfig).
 * Espeja la semántica de buildExportColumns (ids lógicos): core habilitado (excluye
 * actions/selection y entradas "solo filterable"), calculada `semana` pegada a `fecha`,
 * native habilitado. Tags solo para PUBLICACIONES (DATA EXPORT nunca exporta tags).
 */
export function listAvailableColumns(
  config: ColumnConfigDoc,
  screenId: ColumnTemplateScreenId
): AvailableColumn[] {
  const out: AvailableColumn[] = [];
  const core = config.core || {};
  Object.entries(core)
    .filter(([id, e]: any) => {
      if (id === 'actions' || id === 'selection') return false;
      if (e && e.enabled === false) return false;
      // entrada "solo filterable" (ej. subcategory/segmento en core = {filterable:true})
      if (e && e.enabled === undefined && e.order === undefined && e.displayName === undefined) {
        return false;
      }
      return true;
    })
    .sort(([, a]: any, [, b]: any) => (a?.order ?? 9999) - (b?.order ?? 9999))
    .forEach(([id, e]: any) => {
      out.push({ id, label: e?.displayName || id, group: 'core' });
      if (id === 'fecha') {
        out.push({ id: 'semana', label: 'Semana', group: 'calc' });
      }
    });

  Object.entries(config.native || {})
    .filter(([, e]: any) => e && e.enabled === true)
    .sort(([, a]: any, [, b]: any) => (a?.order ?? 9999) - (b?.order ?? 9999))
    .forEach(([id, e]: any) => out.push({ id, label: e?.displayName || id, group: 'native' }));

  if (screenId === 'publicaciones') {
    Object.entries(config.tags || {})
      .filter(([, e]: any) => e && e.enabled === true)
      .sort(([, a]: any, [, b]: any) => (a?.order ?? 9999) - (b?.order ?? 9999))
      .forEach(([id, e]: any) => out.push({ id, label: e?.displayName || id, group: 'tags' }));
  }

  // PT-#67: DATA EXPORT parte de las columnas del reporte de referencia (23 cols). Además de
  // las del Registro, incluye las CALCULADAS inyectadas por el exportador (toma/id_pulpou/url;
  // `semana` ya se pegó a `fecha` arriba) para que el editor las muestre/ordene/togglee. Se
  // ordena por DATA_EXPORT_BASE_ORDER (orden canónico del reporte); columnas extra del cliente
  // no presentes en la base se appendean al final.
  if (screenId === 'data_export') {
    return orderDataExportAvailable(out);
  }

  return out;
}

/** Inyecta las calculadas del DATA EXPORT y ordena la lista disponible por el orden canónico
 *  del reporte de referencia (DATA_EXPORT_BASE_ORDER). */
function orderDataExportAvailable(base: AvailableColumn[]): AvailableColumn[] {
  const byId = new Map<string, AvailableColumn>(base.map((c) => [c.id, c]));
  for (const id of ['toma', 'id_pulpou', 'url']) {
    if (!byId.has(id)) {
      byId.set(id, { id, label: DEFAULT_EXPORT_ATTRS[id]?.title || id, group: 'calc' });
    }
  }
  const ordered: AvailableColumn[] = [];
  const used = new Set<string>();
  for (const id of DATA_EXPORT_BASE_ORDER) {
    const c = byId.get(id);
    if (c) {
      ordered.push(c);
      used.add(id);
    }
  }
  for (const c of base) {
    if (!used.has(c.id)) {
      ordered.push(c);
      used.add(c.id);
    }
  }
  return ordered;
}

// ----- Sortable row -----

interface RowProps {
  item: { id: string; enabled: boolean; order: number };
  ordinal: number; // 1-based, solo cuenta las habilitadas
  label: string;
  group: AvailableColumn['group'];
  onToggle: (id: string, enabled: boolean) => void;
}

const SortableRow: React.FC<RowProps> = ({ item, ordinal, label, group, onToggle }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 px-2 py-1.5 rounded border ${
        item.enabled ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-100'
      }`}
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
        title="Arrastrar para reordenar"
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <span
        className={`inline-flex items-center justify-center w-6 h-5 text-xs font-mono rounded ${
          item.enabled ? 'bg-pink-100 text-pink-700' : 'bg-gray-200 text-gray-400'
        }`}
      >
        {item.enabled ? ordinal : '—'}
      </span>
      <span className={`text-sm flex-1 min-w-0 truncate ${item.enabled ? 'text-gray-800' : 'text-gray-400'}`}>
        {label}
      </span>
      <span className={`text-[10px] px-1.5 py-0.5 rounded border ${GROUP_BADGE[group]}`}>
        {GROUP_LABEL[group]}
      </span>
      <button
        onClick={() => onToggle(item.id, !item.enabled)}
        className={`p-1 rounded ${item.enabled ? 'text-pink-600 hover:bg-pink-50' : 'text-gray-400 hover:bg-gray-100'}`}
        title={item.enabled ? 'Ocultar en esta pantalla' : 'Mostrar en esta pantalla'}
      >
        {item.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
      </button>
    </div>
  );
};

const ColumnTemplatesEditor: React.FC<Props> = ({ config, clientId, lastTomaScanned, onChange }) => {
  const { t } = useTranslation();
  const [screenId, setScreenId] = useState<ColumnTemplateScreenId>('publicaciones');
  const [showPreview, setShowPreview] = useState(false);
  const [previewRows, setPreviewRows] = useState<any[] | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const available = useMemo(() => listAvailableColumns(config, screenId), [config, screenId]);
  const availableIds = useMemo(() => available.map((c) => c.id), [available]);
  const labelById = useMemo(() => {
    const m = new Map<string, AvailableColumn>();
    available.forEach((c) => m.set(c.id, c));
    return m;
  }, [available]);

  const storedTemplate: ColumnTemplate | undefined = config.templates?.[screenId];

  // Items reconciliados contra las columnas disponibles actuales (conserva
  // enabled/order existentes, appendea nuevas, descarta inexistentes).
  const items = useMemo(
    () => reconcileTemplateItems(availableIds, storedTemplate),
    [availableIds, storedTemplate]
  );

  const commit = (newItems: { id: string; enabled: boolean; order: number }[]) => {
    const normalized = newItems.map((it, i) => ({ ...it, order: i }));
    const nextMap: ColumnTemplatesMap = {
      ...(config.templates || {}),
      [screenId]: { items: normalized, updatedAt: new Date().toISOString() },
    };
    onChange(nextMap);
  };

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((it) => it.id === active.id);
    const newIndex = items.findIndex((it) => it.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    commit(arrayMove(items, oldIndex, newIndex));
  };

  const handleToggle = (id: string, enabled: boolean) => {
    commit(items.map((it) => (it.id === id ? { ...it, enabled } : it)));
  };

  const enabledCount = items.filter((it) => it.enabled).length;

  // Preview: traer 50 filas reales de la última toma (fallback a sample sintético).
  useEffect(() => {
    if (!showPreview) return;
    if (previewRows !== null) return;
    if (!lastTomaScanned) {
      setPreviewRows([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setPreviewLoading(true);
      try {
        const res = await api.get(`/api/tables/${lastTomaScanned}/data?limit=50`, {
          headers: { 'x-client-id': clientId },
        });
        const rows = res.data?.data;
        if (!cancelled) setPreviewRows(Array.isArray(rows) ? rows : []);
      } catch (err) {
        console.warn('[ColumnTemplatesEditor] preview rows fetch failed, using synthetic sample:', err);
        if (!cancelled) setPreviewRows([]);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showPreview, lastTomaScanned, clientId, previewRows]);

  // Orden/visibilidad efectivos del template para el preview (ids visibles ordenados).
  const previewOrder = useMemo(
    () => resolveTemplateOrder(availableIds, { items }),
    [availableIds, items]
  );

  const useReal = Array.isArray(previewRows) && previewRows.length > 0;
  const rowsToShow = useReal ? previewRows! : ((previewSample as any).rows || []);

  return (
    <div>
      <p className="text-xs text-gray-600 bg-indigo-50 border border-indigo-100 rounded p-2 mb-3">
        {t(
          'admin.columnConfig.templates.intro',
          'Los Templates definen qué columnas se muestran y en qué orden para cada pantalla. PUBLICACIONES impacta el listado y su export; DATA EXPORT es la base del módulo Exportar. El Registro solo determina qué columnas están disponibles.'
        )}
      </p>

      {/* Selector de template + preview toggle */}
      <div className="flex items-center gap-2 mb-3">
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          {TEMPLATE_SCREENS.map((s) => (
            <button
              key={s.id}
              onClick={() => {
                setScreenId(s.id);
                setShowPreview(false);
                setPreviewRows(null);
              }}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md flex items-center gap-1.5 transition-colors ${
                screenId === s.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
              title={s.description}
            >
              <Layers className="w-3.5 h-3.5" />
              {s.label}
            </button>
          ))}
        </div>
        <span className="text-xs text-gray-500">
          {enabledCount} {t('admin.columnConfig.templates.ofColumns', 'de')} {items.length}{' '}
          {t('admin.columnConfig.templates.columns', 'columnas')}
        </span>
        <button
          onClick={() => setShowPreview((v) => !v)}
          className={`ml-auto px-3 py-1.5 text-xs font-medium rounded-md flex items-center gap-1.5 ${
            showPreview
              ? 'bg-indigo-100 text-indigo-700 ring-1 ring-indigo-300'
              : 'text-indigo-700 bg-indigo-50 hover:bg-indigo-100'
          }`}
        >
          <Eye className="w-3.5 h-3.5" />
          {showPreview
            ? t('admin.columnConfig.templates.hidePreview', 'Ocultar preview')
            : t('admin.columnConfig.templates.showPreview', 'Ver preview')}
        </button>
      </div>

      <p className="text-xs text-gray-500 mb-2">
        {TEMPLATE_SCREENS.find((s) => s.id === screenId)?.description}
      </p>

      {/* Lista sortable */}
      {items.length === 0 ? (
        <div className="text-sm text-gray-500 italic py-6 text-center border border-dashed border-gray-200 rounded">
          {t(
            'admin.columnConfig.templates.empty',
            'No hay columnas habilitadas en el Registro para esta pantalla.'
          )}
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={items.map((it) => it.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-1.5">
              {(() => {
                let ord = 0;
                return items.map((it) => {
                  const meta = labelById.get(it.id);
                  if (it.enabled) ord += 1;
                  return (
                    <SortableRow
                      key={it.id}
                      item={it}
                      ordinal={ord}
                      label={meta?.label || it.id}
                      group={meta?.group || 'core'}
                      onToggle={handleToggle}
                    />
                  );
                });
              })()}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Preview con datos reales aplicando el orden/visibilidad del template */}
      {showPreview && (
        <div className="mt-4 border-t pt-3">
          {previewLoading ? (
            <div className="flex items-center justify-center py-10 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              {t('admin.columnConfig.templates.previewLoading', 'Cargando datos del cliente…')}
            </div>
          ) : (
            <>
              <p className="text-xs text-gray-500 italic mb-2">
                {useReal
                  ? t('admin.columnConfig.templates.previewReal', 'Datos reales — primeras 50 filas de {{table}}', {
                      table: lastTomaScanned,
                    })
                  : t('admin.columnConfig.templates.previewSynthetic', 'Datos sintéticos de ejemplo.')}
              </p>
              <DataTable
                tableName={useReal ? (lastTomaScanned || '__preview__') : '__preview__'}
                clientId={clientId}
                previewMode
                previewSampleRows={rowsToShow}
                columnConfigOverride={config}
                templateColumnOrder={previewOrder}
                hideFilters
                enableTags={screenId === 'publicaciones'}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ColumnTemplatesEditor;
